import {
  CellClassParams,
  ColDef,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GetContextMenuItemsParams,
  GridOptions,
  GridReadyEvent,
  ISetFilterParams,
  ITooltipParams,
  MenuItemDef,
  ModelUpdatedEvent,
  Module,
  RowDataChangedEvent,
  RowDoubleClickedEvent,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import { DOCUMENT } from '@angular/common'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  disableChangeDetectionOnScroll,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  enableHeliosMultiRowDragSelection,
  generateHeliosAgGridSidebarFromScratch,
  generateMdiIcon,
  getFilterOnCellContextMenu,
  heliosClientSideRowConfig,
  HeliosColumnTypes,
  heliosDefaultColWidth,
  heliosDefaultToolPanels,
  heliosMultiRowSelectionConfig,
  heliosSizingAdjustments,
} from '@helios/ag-grid-config'
import { formatDates } from '@helios/common-api'
import {
  AdvancedOptionsParams,
  FilterModalEvent,
  FilterModalService,
} from '@helios/filter-modal'
import {
  RootState,
  SavedView,
  selectActiveSessionIdentifier,
  selectActiveSessionReconDate,
  selectIsHplAccount,
  selectLegacyUserId,
  selectAnyCreatePermission,
} from '@helios/state-management'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
  WorkspaceUpdateEventObject,
} from '@helios/user-preferences'
import { Store } from '@ngrx/store'
import { AgGridApi, NfColumnTypes, quantityFormatter } from '@northfork/ag-grid'
import { AgGridState, TableLayoutComponent } from '@northfork/ag-grid-angular'
import {
  composeFieldPath,
  formatDate,
  isDateBefore,
  ISODateFormat,
} from '@northfork/basics'
import { Daterange } from '@northfork/ng-basics'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { createPanelSettingsToolPanel } from 'libs/generic-components/src/lib/panel-settings-tool-panel/utils/panel-settings-tool-panel-sidebar-utils'
import _ from 'lodash-es'
import { Subject } from 'rxjs'
import { map, take, takeUntil } from 'rxjs/operators'
import { DistributionsKey, DistributionsMainGridKey } from '../../../panel-key'
import {
  Distribution,
  DistributionAdvancedOptions,
  QuickEnterPredictionAction,
  tranTypeCodes,
} from '../../models/distribution.model'
import { DistributionsAdvancedOptionsComponent } from '../distributions-advanced-options/distributions-advanced-options.component'

/**
 * DO NOT CHANGE. Saved Views and Filter Groups depend on this key. Changing will result in users losing their saved
 * preferences for this grid.
 */
const DistributionsMainGridRowClass = `${DistributionsKey}-row`

const distributionPath = composeFieldPath([] as Distribution[])

@Component({
  selector: 'hs-distributions-grid[data][windowDate]',
  templateUrl: './distributions-grid.component.html',
  styleUrls: ['./distributions-grid.component.scss'],
  providers: [FilterModalService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DistributionsGridComponent implements OnDestroy, OnInit {
  @ViewChild('table')
  readonly table?: TableLayoutComponent<Distribution>

  /** @internal */
  gridApi?: AgGridApi

  @Input() data: Distribution[] | null = null
  @Input() windowDate: Date | null = null
  @Input() dateRange: Daterange | null = null
  @Output() dateRangeChange = new EventEmitter<Daterange>()
  filterCount = 0
  private _filterState: { [columnName: string]: unknown } | undefined
  clearFiltersOnAccountLoadToggle = new FormControl(true)
  @Input() set savedViews(savedViewsStates: SavedView[] | null | undefined) {
    this._filterState = savedViewsStates?.find(
      view => view.gridKey === DistributionsMainGridKey,
    )?.savedView.filterState
  }

  @Output() enterPredictionAction = new EventEmitter<Distribution[]>()
  @Output() quickEnterPredictionAction =
    new EventEmitter<QuickEnterPredictionAction>()
  @Output() editTransactionAction = new EventEmitter<Distribution[]>()

  readonly gridModules: Module[] = [
    FiltersToolPanelModule,
    RangeSelectionModule,
  ]
  applyHPL = false
  gridOptions: GridOptions = this.createGridOptions()

  clearFiltersEnabled = false
  anyCreatePermission = false
  sessionId?: string
  userId?: number
  sessionReconDate?: Date
  hideFutureGroupEntryDatesToggle = new FormControl(false)

  private readonly destroySubject$ = new Subject<void>()

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    // @internal
    public filterModalService: FilterModalService,
    private readonly preferences: UserPreferencesService,
    private readonly store: Store<RootState>,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    this.store
      .select(selectIsHplAccount)
      .pipe(take(1))
      .subscribe(applyHPL => {
        this.applyHPL = applyHPL
        this.gridOptions = this.createGridOptions()
      })
  }

  ngOnDestroy() {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.api?.destroy()

    this.filterModalService.destroy()
  }

  ngOnInit(): void {
    this.store
      .select(selectAnyCreatePermission)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(allowed => {
        this.anyCreatePermission = allowed
      })

    this.store
      .select(selectActiveSessionIdentifier)
      .pipe(take(1))
      .subscribe(sessionId => {
        this.sessionId = sessionId
      })

    this.store
      .select(selectLegacyUserId)
      .pipe(take(1))
      .subscribe(userId => {
        this.userId = userId
      })

    this.store
      .select(selectActiveSessionReconDate)
      .subscribe(sessionReconDate => {
        this.sessionReconDate = sessionReconDate
      })

    this.hideFutureGroupEntryDatesToggle.valueChanges.subscribe(value => {
      this.setHideFutureGroupEntryDatesFilter(value)
    })
    this.clearFiltersOnAccountLoadToggle.valueChanges.subscribe(() => {
      this.updateGridConfig()
    })
  }

  private createGridOptions(): GridOptions {
    return {
      columnDefs: this.createBaseColumnDefs(),
      ...heliosClientSideRowConfig,
      ...heliosMultiRowSelectionConfig,
      getContextMenuItems: params => this.getContextMenuItems(params),
      onRowDataChanged: event => this.onRowDataChanged(event),
      ...heliosSizingAdjustments,
      ...disableChangeDetectionOnScroll(this.changeDetector),
      onGridReady: (event: GridReadyEvent) => this.onGridReady(event),
      onFilterChanged: event => this.filterChanged(event),
      onRowDoubleClicked: event => this.onDoubleClick(event),
      onSelectionChanged: () => this.onRowSelectionChanged(),
      getRowClass: params => [
        'distribution-' + params.node.id,
        DistributionsMainGridRowClass,
      ],
      icons: {
        panelSettings: generateMdiIcon('mdi-cog'),
      },
      onModelUpdated: event =>
        event.newData && this._getAndApplyColumnFilter(event),
      onFirstDataRendered: event => this._getAndApplyColumnFilter(event),
      // adding a debounce here so it doesn't get call a lot of times when scrolling fast
      onBodyScroll: _.debounce(() => this.onRowSelectionChanged(true), 50),
      sideBar: generateHeliosAgGridSidebarFromScratch([
        createPanelSettingsToolPanel({
          controls: [
            {
              label: 'Clear Filters on Load',
              formControl: this.clearFiltersOnAccountLoadToggle,
            },
          ],
        }),
        ...heliosDefaultToolPanels,
      ]),
    }
  }

  updateGridConfig() {
    const panelSettingsChangedEvent = new CustomEvent('panelSettingsChanged', {
      detail: {
        api: this.gridApi,
        panelSettings: this.getCurrentPanelSettingsObject(),
      } as WorkspaceUpdateEventObject,
    })
    this.gridApi?.api.dispatchEvent(panelSettingsChangedEvent)
  }

  getCurrentPanelSettingsObject() {
    const panelSettings: { [key: string]: unknown } = {}
    panelSettings['clearFiltersOnAccountLoad'] =
      this.clearFiltersOnAccountLoadToggle.value
    return panelSettings
  }

  private applyGridConfig(agGridStateSaved: AgGridState) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const panelSettings = agGridStateSaved.panelSettings as any

    if (panelSettings) {
      if ('clearFiltersOnAccountLoad' in panelSettings) {
        this.clearFiltersOnAccountLoadToggle.patchValue(
          Boolean(panelSettings.clearFiltersOnAccountLoad),
          { emitEvent: false },
        )
      }
    }
  }

  private _getAndApplyColumnFilter(
    event: ModelUpdatedEvent | FirstDataRenderedEvent,
  ): void {
    if (!event.api.isColumnFilterPresent()) {
      if (this._filterState) {
        // Apply preserved filters once grid is rendered
        event.api.setFilterModel(this._filterState)
        setTimeout(() => {
          event.api.onFilterChanged()
        })
      }
    }
  }

  /**
   * We are taking this approach due to the slowness of aggrid when rendering the highlighted rows.
   *
   * @param ignoreClassRemove
   */
  onRowSelectionChanged(ignoreClassRemove: boolean = false) {
    if (!ignoreClassRemove) {
      const groupHighlight = this.document.querySelectorAll(
        `.group-highlight.${DistributionsMainGridRowClass}`,
      )
      groupHighlight.forEach(e => e.classList.remove('group-highlight'))
    }
    const rows: Distribution[] = this.gridApi?.api.getSelectedRows() ?? []
    let idsToHighlight: string[] = []
    this.gridApi?.api.forEachNode(node => {
      const isInGroup = this.checkGroupCondition(rows[0], node.data)
      if (isInGroup && node.id != null) {
        idsToHighlight.push(node.id)
      }
    })
    const rowSelected = this.gridApi?.api
      ?.getSelectedNodes()
      ?.map(selected => selected.id)

    idsToHighlight = idsToHighlight.filter(x => !rowSelected?.includes(x))
    if (idsToHighlight.length > 1) {
      idsToHighlight.forEach(r => {
        const nodeIds = this.document.querySelectorAll(`.distribution-${r}`)
        nodeIds.forEach(element => element.classList.add('group-highlight'))
      })
    }
  }

  /** @internal */
  checkGroupCondition(
    selectedDistribution: Distribution,
    distribution: Distribution,
  ) {
    return (
      distribution &&
      selectedDistribution &&
      distribution.acctId === selectedDistribution.acctId &&
      distribution.assetId === selectedDistribution.assetId &&
      distribution.tranTypeCode === selectedDistribution.tranTypeCode &&
      distribution.calculatedEntryDate ===
        selectedDistribution.calculatedEntryDate
    )
  }

  private onDoubleClick(event: RowDoubleClickedEvent) {
    if (!this.isEditTransactionDisabled(event.data)) {
      this.editTransactionAction.emit(event.data)
    } else if (!this.isEnterPredictionDisabled(event.data)) {
      this.enterPredictionAction.emit(event.data)
    }
  }

  /** @internal */
  async onGridReady(event: GridReadyEvent) {
    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(event.api, DensityLevel.Compact === level),
      )
    enableHeliosCopyBehavior(event.api)
    enableHeliosMultiRowDragSelection(event.api)

    this.filterModalService
      .registerGrid(
        event.api,
        DistributionsKey,
        DistributionsMainGridKey,
        [],
        DistributionsAdvancedOptionsComponent,
        () => ({
          context: {},
          dateRange: this.dateRange,
        }),
      )
      .pipe(
        takeUntil(this.destroySubject$),
        map(
          x =>
            x as FilterModalEvent<
              AdvancedOptionsParams<unknown, DistributionAdvancedOptions>
            >,
        ),
      )
      .subscribe(changes => {
        this.dateRangeChange.emit(changes.payload.dateRange)
      })

    this.gridApi = event

    /* why move this later ? so that all filter change events are called before workspace is rendered and only if workspace
    has filter model it ever again calls filter model change not otherwise avoiding unnecessary race conditions */
    const agGridStateSaved = await this.savedViewService.manageSavedView(
      event,
      DistributionsMainGridKey,
      this.destroySubject$,
      true,
      this.getCurrentPanelSettingsObject(),
    )

    this.applyGridConfig(agGridStateSaved)
  }

  /** Right-click menu */
  private getContextMenuItems(
    params: GetContextMenuItemsParams,
  ): MenuItemDef[] {
    // select the row
    if (params.node && !params.node.isSelected()) {
      params.api.selectNode(params.node)
    }

    const distributionClicked: Distribution[] = params.api.getSelectedRows()
    const suffix =
      distributionClicked.length > 1
        ? ' (' + distributionClicked.length + ')'
        : ''
    const result: MenuItemDef[] = [
      {
        name: 'Enter Prediction' + suffix, // Launches distributions coupon entry modal
        disabled:
          !this.anyCreatePermission ||
          this.isEnterPredictionDisabled(distributionClicked),
        action: () => {
          this.enterPredictionAction.emit(distributionClicked)
        },
        tooltip: !this.anyCreatePermission
          ? 'Feature not available due to lack of permission'
          : this.isEntryDateBeforeWindowDate(distributionClicked)
          ? 'Cannot enter prediction before window date'
          : !distributionClicked.some(dist => {
              return dist.isCalculated
            })
          ? 'Cannot enter prediction for non-calculated distribution'
          : '',
      },
      {
        name: 'Quick Enter Prediction' + suffix, // Doesn't Launches distributions coupon entry modal, directly saves command
        disabled:
          !this.anyCreatePermission ||
          this.isEnterPredictionDisabled(distributionClicked),
        action: () => {
          const quickEnterPredictionData: QuickEnterPredictionAction = {
            distributionClicked: distributionClicked,
            sessionId: this.sessionId,
            userId: this.userId,
            sessionReconDate: this.sessionReconDate,
          }
          this.quickEnterPredictionAction.emit(quickEnterPredictionData)
        },
        tooltip: !this.anyCreatePermission
          ? 'Feature not available due to lack of permission'
          : this.isEntryDateBeforeWindowDate(distributionClicked)
          ? 'Cannot enter prediction before window date'
          : !distributionClicked.some(dist => {
              return dist.isCalculated
            })
          ? 'Cannot enter prediction for non-calculated distribution'
          : '',
      },
      {
        name: 'Edit Transaction' + suffix, // Launches edit transaction entry modal
        disabled:
          !this.anyCreatePermission ||
          this.isEditTransactionDisabled(distributionClicked),
        action: () => {
          if (distributionClicked.length === 0) {
            throw new Error('Distribution is null')
          }
          if (
            distributionClicked.some(dist => {
              return dist.tranId == undefined
            })
          ) {
            throw new Error('Distribution tranId is null')
          }
          this.editTransactionAction.emit(distributionClicked)
        },
        tooltip: !this.anyCreatePermission
          ? 'Feature not available due to lack of permission'
          : !distributionClicked.some(dist => {
              return dist.actualAmount
            })
          ? 'Cannot edit a distribution that is not linked to an actual transaction'
          : this.isEntryDateBeforeWindowDate(distributionClicked)
          ? 'Cannot edit transaction before window date'
          : '',
      },
      ...getFilterOnCellContextMenu(params),
    ]
    return result
  }

  onRowDataChanged(event: RowDataChangedEvent) {
    if (this.data != null) {
      return
    }
    event.api.showLoadingOverlay()
  }

  private createBaseColumnDefs(): ColDef[] {
    // helper functions
    const cellClassFn = (params: CellClassParams) =>
      params.value !== 0 ? ' text-danger' : ''
    const dateFormatterParams = (params: ValueFormatterParams) =>
      formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
    const tooltipDateFormatterParams = (params: ITooltipParams) =>
      formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy')

    return [
      {
        headerName: 'Status',
        field: distributionPath('breakStatus'),
        hide: false,
        type: HeliosColumnTypes.BreakStatus,
      },
      {
        field: distributionPath('matchedStatus'),
        hide: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          refreshValuesOnOpen: true,
          defaultToNothingSelected: true,
          values: ['MATCHED', 'ACTUAL_ONLY', 'CALCULATED_ONLY'],
          buttons: ['apply', 'reset'],
          closeOnApply: true,
        } as ISetFilterParams,
      },
      {
        field: distributionPath('acctId'),
        headerName: 'Account ID',
        type: NfColumnTypes.Identifier,
        width: heliosDefaultColWidth.Id,
        hide: true,
        sortIndex: 1,
        filter: 'agSetColumnFilter',
        filterParams: {
          defaultToNothingSelected: true,
          buttons: ['apply', 'reset'],
          closeOnApply: true,
        } as ISetFilterParams,
      },
      {
        field: distributionPath('entryDate'),
        headerName: 'Group Entry Date',
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
        minWidth: 0,
        sortIndex: 2,
        sort: 'desc',
        valueFormatter: dateFormatterParams,
        tooltipValueGetter: tooltipDateFormatterParams,
      },
      {
        field: distributionPath('settleDate'),
        headerName: 'Group Settle Date',
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
        minWidth: 0,
        sortIndex: 2,
        sort: 'desc',
        hide: true,
        valueFormatter: dateFormatterParams,
        tooltipValueGetter: tooltipDateFormatterParams,
      },
      {
        field: distributionPath('postDate'),
        headerName: 'Group Post Date',
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
        minWidth: 0,
        sortIndex: 2,
        sort: 'desc',
        hide: true,
        valueFormatter: dateFormatterParams,
        tooltipValueGetter: tooltipDateFormatterParams,
      },
      {
        field: distributionPath('calculatedEntryDate'),
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
        minWidth: 0,
        hide: true,
        valueFormatter: dateFormatterParams,
        tooltipValueGetter: tooltipDateFormatterParams,
      },
      {
        field: distributionPath('calculatedSettleDate'),
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
        minWidth: 0,
        hide: true,
        valueFormatter: dateFormatterParams,
        tooltipValueGetter: tooltipDateFormatterParams,
      },
      {
        field: distributionPath('actualEntryDate'),
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
        minWidth: 0,
        hide: true,
        valueFormatter: dateFormatterParams,
        tooltipValueGetter: tooltipDateFormatterParams,
      },
      {
        field: distributionPath('actualSettleDate'),
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
        minWidth: 0,
        hide: true,
        valueFormatter: dateFormatterParams,
        tooltipValueGetter: tooltipDateFormatterParams,
      },
      {
        field: distributionPath('calculatedPostDate'),
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
        minWidth: 0,
        valueFormatter: dateFormatterParams,
        tooltipValueGetter: tooltipDateFormatterParams,
      },
      {
        field: distributionPath('actualPostDate'),
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
        minWidth: 0,
        valueFormatter: dateFormatterParams,
        tooltipValueGetter: tooltipDateFormatterParams,
      },
      {
        field: distributionPath('clearedDate'),
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
        minWidth: 0,
        valueFormatter: dateFormatterParams,
        tooltipValueGetter: tooltipDateFormatterParams,
      },
      {
        field: distributionPath('calculatedAmountTotal'),
        type: this.applyHPL
          ? HeliosColumnTypes.HPLNumber
          : NfColumnTypes.Number,
      },
      {
        field: distributionPath('calculatedAmount'),
        type: this.applyHPL
          ? HeliosColumnTypes.HPLNumber
          : NfColumnTypes.Number,
      },
      {
        field: distributionPath('actualAmount'),
        type: this.applyHPL
          ? HeliosColumnTypes.HPLNumber
          : NfColumnTypes.Number,
      },
      {
        field: distributionPath('calculatedPrepaidInterest'),
        type: this.applyHPL
          ? HeliosColumnTypes.HPLNumber
          : NfColumnTypes.Number,
      },
      {
        field: distributionPath('actualPrepaidInterest'),
        type: this.applyHPL
          ? HeliosColumnTypes.HPLNumber
          : NfColumnTypes.Number,
      },
      {
        field: distributionPath('actualAmountTotal'),
        type: this.applyHPL
          ? HeliosColumnTypes.HPLNumber
          : NfColumnTypes.Number,
      },
      {
        field: distributionPath('amountTotalDifference'),
        type: NfColumnTypes.Number,
        cellClass: params => {
          return Math.abs(Math.trunc(params.value * 1000) / 1000) >= 0.005
            ? ' text-danger'
            : ''
        },
      },
      {
        field: distributionPath('entryDateDifference'),
        type: NfColumnTypes.Identifier,
        width: heliosDefaultColWidth.Id,
        cellClass: cellClassFn,
      },
      {
        field: distributionPath('settleDateDifference'),
        hide: true,
        type: NfColumnTypes.Identifier,
        width: heliosDefaultColWidth.Id,
        cellClass: cellClassFn,
      },
      {
        field: distributionPath('postDateDifference'),
        hide: true,
        type: NfColumnTypes.Identifier,
        width: heliosDefaultColWidth.Id,
        cellClass: cellClassFn,
      },
      {
        field: distributionPath('tranTypeCode'),
        width: heliosDefaultColWidth.MediumIcon,
        filter: 'agSetColumnFilter',
        filterParams: {
          defaultToNothingSelected: true,
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          values: tranTypeCodes,
        } as ISetFilterParams,
      },
      {
        field: distributionPath('rate'),
        type: HeliosColumnTypes.PreciseNumber,
        width: heliosDefaultColWidth.Id,
        // Marks rates sourced from LLA. Delegates to the PreciseNumber formatter so the asterisk is the
        // only change — formatting the value here would alter every non-LLA rate too.
        valueFormatter: params =>
          params.value == null
            ? ''
            : `${quantityFormatter(params)}${
                params.data?.isLlaRate ? '*' : ''
              }`,
      },
      {
        field: distributionPath('factor'),
        type: NfColumnTypes.Identifier,
        width: heliosDefaultColWidth.Id,
      },
      {
        field: distributionPath('originalUnits'),
        type: this.applyHPL
          ? HeliosColumnTypes.HPLNumber
          : NfColumnTypes.Number,
        headerName: 'Original Units',
      },
      {
        field: distributionPath('factorizedUnits'),
        type: this.applyHPL
          ? HeliosColumnTypes.HPLNumber
          : NfColumnTypes.Number,
        headerName: 'Factorized Units',
      },
      {
        field: distributionPath('isCalculated'),
        type: HeliosColumnTypes.Boolean,
        width: heliosDefaultColWidth.SmallIcon,
      },
      {
        field: distributionPath('legacyOrigId'),
        headerName: 'Orig Transaction ID',
        type: NfColumnTypes.Identifier,
        width: heliosDefaultColWidth.Id,
      },
      { field: distributionPath('loss'), type: NfColumnTypes.Number },
      {
        field: distributionPath('secId'),
        type: NfColumnTypes.Identifier,
        headerName: 'Security ID',
        width: heliosDefaultColWidth.Id,
      },
      {
        field: distributionPath('assetId'),
        headerName: 'Asset ID',
        width: heliosDefaultColWidth.Id,
        filter: false,
      },
      {
        field: distributionPath('tranId'),
        headerName: 'Transaction UUID',
        width: heliosDefaultColWidth.Id,
      },
      {
        field: distributionPath('inventoryType'),
        hide: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          refreshValuesOnOpen: true,
          defaultToNothingSelected: true,
          values: ['LONG', 'SHORT', 'ISSUE'],
          buttons: ['apply', 'reset'],
          closeOnApply: true,
        } as ISetFilterParams,
      },
      {
        field: distributionPath('collateralBroker'),
        filter: 'agSetColumnFilter',
        filterParams: {
          defaultToNothingSelected: true,
          buttons: ['apply', 'reset'],
          closeOnApply: true,
        } as ISetFilterParams,
      },
      {
        field: distributionPath('paymentCurrencyCode'),
        headerName: 'Payment Currency Code',
      },
    ]
  }

  launchFiltersModal() {
    this.filterModalService.launchFiltersModal()
  }

  private readonly filterChanged = (event: FilterChangedEvent) => {
    this.clearFiltersEnabled = event.api.isAnyFilterPresent() ?? false
    this.filterCount = Object.keys(event.api.getFilterModel()).length
    this.onHideFutureGroupEntryDateFilterChanged(event)
  }

  clearFilters() {
    this.gridApi?.api.setFilterModel(null)
  }

  async setHideFutureGroupEntryDatesFilter(value: boolean) {
    const entryDateFilterComponent =
      this.gridApi?.api.getFilterInstance('entryDate')
    if (value && this.sessionReconDate) {
      entryDateFilterComponent?.setModel({
        type: 'lessThanOrEqual',
        filterType: 'date',
        filter: formatDate(this.sessionReconDate, ISODateFormat),
      })
    } else entryDateFilterComponent?.setModel(null)
    //applied timeout so that the onFilterChanged is called once the filter is correctly applied on the column
    await new Promise(f => setTimeout(f, 40))
    this.gridApi?.api.onFilterChanged()
  }

  onHideFutureGroupEntryDateFilterChanged(event: FilterChangedEvent) {
    const filterModel = event.api.getFilterInstance('entryDate')?.getModel()
    const todayDateStr = formatDate(this.sessionReconDate, ISODateFormat)
    const isGroupEntryDateNotFutureFilter =
      filterModel &&
      filterModel.type === 'lessThanOrEqual' &&
      filterModel.filter === todayDateStr
    this.hideFutureGroupEntryDatesToggle.setValue(
      isGroupEntryDateNotFutureFilter,
      { emitEvent: false },
    )
  }

  isEntryDateBeforeWindowDate(rows: Distribution[]) {
    for (const row of rows) {
      if (this.windowDate != null && row.actualEntryDate) {
        return isDateBefore(row.actualEntryDate, this.windowDate)
      }
    }
    return false
  }

  isEnterPredictionDisabled(rows: Distribution[]) {
    for (const row of rows) {
      if (
        row?.isCalculated == null ||
        !row.isCalculated ||
        this.isEntryDateBeforeWindowDate([row])
      ) {
        return true
      }
    }
    return false
  }

  isEditTransactionDisabled(rows: Distribution[]) {
    for (const row of rows) {
      if (
        row?.actualAmount == null ||
        this.isEntryDateBeforeWindowDate([row])
      ) {
        return true
      }
    }
    return false
  }
}
